import { Grid, Heading, ListItem, Alert, AlertIcon, AlertTitle, AlertDescription, Button } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import type { StatsIntervalIds } from 'types/client/stats';

import Skeleton from '../shared/chakra/Skeleton';
import ChartWidget from '../shared/chart/ChartWidget';
import formatDate from '../shared/chart/utils/formatIntervalDate';
import { STATS_INTERVALS } from './constants';

type Props = {
  isPlaceholderData: boolean;
  interval: StatsIntervalIds;
};

interface EnergyDataPoint {
  date: string;
  date_to: string;
  value: string;
  is_approximate?: boolean;
}

interface EnergyApiResponse {
  chart: Array<EnergyDataPoint>;
  info: {
    id: string;
    title: string;
    description: string;
    units: string | null;
    resolutions: Array<string>;
  };
}

interface ChartItem {
  date: Date;
  value: number;
  is_approximate?: boolean;
}

// API client function
const fetchEnergyData = async (params: {
  from: string;
  to: string;
  resolution: string;
}): Promise<EnergyApiResponse> => {
  const searchParams = new URLSearchParams({
    from: params.from,
    to: params.to,
    resolution: params.resolution,
  });

  const response = await fetch(
    `https://vtth5qd0e2.execute-api.me-central-1.amazonaws.com/default/adminDashboardEnergyConsumption?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json() as Promise<EnergyApiResponse>;
};

// Custom hook for energy data
const useEnergyData = (params: {
  from: string;
  to: string;
  resolution: string;
}) => {
  return useQuery({
    queryKey: ['energyData', params],
    queryFn: () => fetchEnergyData(params),
    enabled: Boolean(params.from && params.to),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Transform API data to chart items
const transformEnergyDataToChartItems = (energyData: Array<EnergyDataPoint>): Array<ChartItem> => {
  return energyData.map(item => ({
    date: new Date(item.date),
    value: parseFloat(item.value) || 0,
    is_approximate: item.is_approximate,
  }));
};

const ChartEnergyConsumption = ({ interval, isPlaceholderData }: Props) => {
  const selectedInterval = STATS_INTERVALS[interval];

  const endDate = selectedInterval.start ? formatDate(new Date()) : undefined;
  const startDate = selectedInterval.start ? formatDate(selectedInterval.start) : undefined;
  const resolution = 'DAY';

  // Fetch energy data using React Query
  const {
    data: energyData,
    error,
    isLoading: isEnergyLoading,
    isError,
    refetch,
  } = useEnergyData({
    from: startDate || '',
    to: endDate || '',
    resolution,
  });

  // Determine loading state
  const isLoading = isPlaceholderData || isEnergyLoading;

  // Prepare chart items
  let items: Array<ChartItem> = [];
  let chartTitle = 'Energy Consumption';
  let chartDescription = 'Total energy consumption over time';

  if (energyData?.chart) {
    items = transformEnergyDataToChartItems(energyData.chart);
    chartTitle = energyData.info.title || chartTitle;
    chartDescription = energyData.info.description || chartDescription;
  } else if (!isLoading && !isError) {
    items = [];
  }

  const handleRetry = () => {
    refetch();
  };

  return (
    <ListItem
      mb={8}
      _last={{
        marginBottom: 0,
      }}
    >
      <Skeleton isLoaded={!isLoading} mb={4} display="inline-flex" alignItems="center" columnGap={2}>
        <Heading size="md">
          Energy Consumption
        </Heading>
        {energyData?.info.units && (
          <Heading size="sm" color="gray.500">
            ({energyData.info.units})
          </Heading>
        )}
      </Skeleton>

      { /* Error handling */}
      {isError && error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <div>
            <AlertTitle>Failed to load energy data!</AlertTitle>
            <AlertDescription display="block">
              {error.message}
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                ml={2}
                onClick={() => handleRetry()}
              >
                Retry
              </Button>
            </AlertDescription>
          </div>
        </Alert>
      )}

      <Grid
        templateColumns={{ lg: 'repeat(2, minmax(0, 1fr))' }}
        gap={4}
      >
        <ChartWidget
          isError={isError}
          items={items}
          title={chartTitle}
          description={chartDescription}
          isLoading={isLoading}
          minH="230px"
        />
      </Grid>

      { /* Show approximate data warning if present */}
      {energyData?.chart.some(item => item.is_approximate) && (
        <Alert status="info" mt={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Some data points are approximate as they include projected or estimated values.
          </AlertDescription>
        </Alert>
      )}
    </ListItem>
  );
};

export default ChartEnergyConsumption;
